import type { ToolPageKey } from "@/lib/seo";
import type { ComparisonSlug } from "./comparison-data";
import type { LimitsPage } from "./limits-data";

/**
 * Links between content types: help, blog, tools, landing and comparison pages.
 *
 * Google had crawled none of the 18 blog posts, `/blog`, `/tools`, most tools,
 * `/alternatives/buffer` or `/about` (Search Console, 2026-09-27: "Discovered –
 * currently not indexed"). 24 of those 33 URLs had no link in the BODY of any
 * page Google already knew, only the sitemap and the site-wide header and
 * footer, which Google weighs lightly. Each content type linked only to its
 * own kind.
 *
 * A link carries its own anchor text rather than looking the target's title
 * up, so a page that links to a post does not have to bundle the blog's
 * content. `link-graph.test.ts` checks every target exists and that each of
 * those pages keeps at least three body links in, one from a page Google
 * already crawls.
 */

/** Pages a cross-link may point at by path. */
export type CrossLinkPath =
  | "/about"
  | "/ai-agents"
  | "/alternatives"
  | "/client-approvals"
  | "/blog"
  | "/limits"
  | "/bluesky-scheduler"
  | "/facebook-scheduler"
  | "/instagram-scheduler"
  | "/linkedin-scheduler"
  | "/mastodon-scheduler"
  | "/pinterest-scheduler"
  | "/pricing"
  | "/tools"
  | "/tools/bluesky-best-time"
  | "/tools/bulk-csv-checker"
  | "/tools/character-counter"
  | "/tools/content-calendar-2027"
  | "/tools/bluesky-engagement-rate"
  | "/tools/mastodon-best-time"
  | "/tools/mastodon-limits"
  | "/tools/thread-splitter"
  | "/tools/video-checker"
  | "/threads-scheduler"
  | "/tiktok-scheduler"
  | "/twitter-scheduler"
  | "/youtube-scheduler";

export type CrossLink =
  | { blog: string; label: string }
  | { help: string; label: string }
  | { limits: LimitsPage["slug"]; label: string }
  | { compare: ComparisonSlug; label: string }
  | { to: CrossLinkPath; label: string };

/** The URL path a link resolves to, for the link graph and React keys. */
export function crossLinkPath(link: CrossLink): string {
  if ("blog" in link) {
    return `/blog/${link.blog}`;
  }

  if ("help" in link) {
    return `/help/${link.help}`;
  }

  if ("limits" in link) {
    return `/limits/${link.limits}`;
  }

  if ("compare" in link) {
    return `/alternatives/${link.compare}`;
  }

  return link.to;
}

/**
 * The home page's "Guides and free tools" section. The home page is the most
 * crawled URL the site has, so what it links to is found first.
 */
export const HOME_GUIDE_LINKS: readonly CrossLink[] = [
  {
    blog: "best-time-to-post-on-bluesky",
    label: "We analysed 8,373 Bluesky posts: posting time barely mattered",
  },
  {
    blog: "schedule-facebook-posts-without-business-suite",
    label: "Schedule Facebook Page posts without Meta Business Suite",
  },
  {
    blog: "why-scheduled-posts-fail",
    label: "Why scheduled posts fail, and what to do about each cause",
  },
  {
    blog: "social-media-content-calendar",
    label: "A social media content calendar that survives a real week",
  },
  {
    blog: "how-to-schedule-mastodon-posts",
    label: "How to schedule Mastodon posts",
  },
  {
    to: "/tools/character-counter",
    label: "Character counter for every network",
  },
  { to: "/tools/thread-splitter", label: "Thread splitter" },
  { to: "/tools/video-checker", label: "Video format checker" },
  { to: "/tools/mastodon-limits", label: "Mastodon limit checker" },
  {
    to: "/tools/content-calendar-2027",
    label: "2027 content calendar template",
  },
  { to: "/blog", label: "All guides on the blog" },
  { to: "/tools", label: "All free tools" },
  { to: "/about", label: "About PostDaily" },
];

/** The `/help` hub's "Guides" strip: longer reads beside the product docs. */
export const HELP_HUB_GUIDE_LINKS: readonly CrossLink[] = [
  {
    blog: "how-to-schedule-bluesky-posts",
    label: "How to schedule Bluesky posts",
  },
  {
    blog: "cross-post-bluesky-mastodon",
    label: "How to cross-post between Bluesky and Mastodon",
  },
  {
    blog: "moving-from-buffer",
    label: "Moving from Buffer without losing your queue",
  },
  {
    blog: "client-approval-workflow-freelancers",
    label: "An approval workflow for freelancers with one client",
  },
  {
    blog: "bluesky-vs-mastodon-vs-threads",
    label: "Bluesky vs Mastodon vs Threads: which should you post on?",
  },
  {
    blog: "what-is-the-fediverse",
    label: "What is the fediverse? A plain guide",
  },
  { to: "/blog", label: "Every guide on the blog" },
  { to: "/tools", label: "Free tools, no account needed" },
  { to: "/about", label: "About PostDaily" },
];

/** Under the pricing page's plans: the honest comparisons. */
export const PRICING_COMPARISON_LINKS: readonly CrossLink[] = [
  { compare: "buffer", label: "PostDaily vs Buffer" },
  { compare: "hootsuite", label: "PostDaily vs Hootsuite" },
  { compare: "sprout-social", label: "PostDaily vs Sprout Social" },
  { compare: "later", label: "PostDaily vs Later" },
  { compare: "publer", label: "PostDaily vs Publer" },
  { compare: "planable", label: "PostDaily vs Planable" },
  { compare: "socialbee", label: "PostDaily vs SocialBee" },
  { compare: "metricool", label: "PostDaily vs Metricool" },
  { compare: "meta-business-suite", label: "PostDaily vs Meta Business Suite" },
  { to: "/alternatives", label: "Every comparison, with who each tool suits" },
  { to: "/client-approvals", label: "Client approval across 10 schedulers" },
];

/** Each free tool's "Keep reading" block. */
export const TOOL_CROSS_LINKS: Readonly<
  Record<ToolPageKey, readonly CrossLink[]>
> = {
  bulkCsvChecker: [
    {
      to: "/tools/content-calendar-2027",
      label: "2027 content calendar template",
    },
    { help: "bulk-upload-csv", label: "Bulk schedule social posts from a CSV" },
    { to: "/pinterest-scheduler", label: "Bulk upload Pins to a board" },
    { to: "/tools", label: "All free tools" },
  ],
  contentCalendar2027: [
    {
      to: "/tools/bulk-csv-checker",
      label: "Check the CSV before you upload it",
    },
    {
      blog: "social-media-content-calendar",
      label: "A content calendar that survives a real week",
    },
    { help: "plan-with-the-calendar", label: "Plan your week on the calendar" },
    { to: "/tools", label: "All free tools" },
  ],
  blueskyBestTime: [
    {
      blog: "best-time-to-post-on-bluesky",
      label: "The study behind this tool: 8,373 Bluesky posts",
    },
    {
      blog: "how-often-to-post-on-bluesky",
      label: "How often should you post on Bluesky?",
    },
    {
      help: "find-your-best-time-to-post",
      label: "Let the AI Assistant pick a posting time",
    },
    { to: "/tools", label: "All free tools" },
  ],
  blueskyEngagementRate: [
    {
      blog: "bluesky-analytics-what-you-can-measure",
      label: "Bluesky analytics: what you can actually measure",
    },
    {
      to: "/bluesky-scheduler",
      label: "Schedule Bluesky posts with PostDaily",
    },
    { to: "/tools", label: "All free tools" },
  ],
  mastodonBestTime: [
    {
      blog: "best-time-to-post-on-mastodon",
      label: "The best time to post on Mastodon, and why we did not measure it",
    },
    {
      blog: "mastodon-for-project-accounts",
      label: "Running a Mastodon account for a project",
    },
    { to: "/tools", label: "All free tools" },
  ],
  mastodonLimits: [
    {
      blog: "mastodon-character-limit",
      label: "The Mastodon character limit, and why your server may disagree",
    },
    {
      help: "character-limits-by-network",
      label: "How each network counts characters",
    },
    {
      to: "/mastodon-scheduler",
      label: "Schedule Mastodon posts with PostDaily",
    },
    { to: "/tools", label: "All free tools" },
  ],
  characterCounter: [
    { to: "/tools/thread-splitter", label: "Split a long draft into a thread" },
    {
      help: "character-limits-by-network",
      label: "How each network counts characters",
    },
    { limits: "instagram", label: "Instagram caption and hashtag limits" },
    {
      to: "/tools/mastodon-limits",
      label: "Your Mastodon server's real limit",
    },
    { to: "/tools", label: "All free tools" },
  ],
  threadSplitter: [
    {
      to: "/tools/character-counter",
      label: "Check one post against every network",
    },
    { to: "/twitter-scheduler", label: "Schedule X (Twitter) threads" },
    { to: "/threads-scheduler", label: "Schedule Threads chains" },
    {
      blog: "bluesky-threads-guide",
      label: "Bluesky threads: how to write one people actually read",
    },
    {
      help: "character-limits-by-network",
      label: "How each network counts characters",
    },
    { to: "/tools", label: "All free tools" },
  ],
  videoChecker: [
    {
      limits: "youtube-shorts",
      label: "YouTube Shorts length and size limits",
    },
    { limits: "tiktok", label: "TikTok video size and length limits" },
    { to: "/tiktok-scheduler", label: "Schedule TikTok videos" },
    { to: "/youtube-scheduler", label: "Schedule YouTube Shorts" },
    { to: "/instagram-scheduler", label: "Schedule Instagram Reels" },
    {
      help: "bluesky-video-not-uploading",
      label: "Bluesky video uploads are failing",
    },
    {
      blog: "why-scheduled-posts-fail",
      label: "Why scheduled posts fail",
    },
    { to: "/tools", label: "All free tools" },
  ],
};
